import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  type Timestamp,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { db, functions, storage } from '../lib/firebase';
import { assertPptAllowed, explainPptRefusal, savePptRecord } from './pptQuota';

/**
 * Progress update folders, shared with everyone in the organisation.
 *
 * Folders and file records live under the organisation; the files themselves
 * sit in the uploader's own storage area, opened by everyone else through the
 * download URL on the record (the same arrangement as experiment attachments).
 */

export type UpdateKind = 'pdf' | 'ppt' | 'pptx';

export type UpdateFolder = {
  id: string;
  name: string;
  createdByUid: string;
  createdByName: string | null;
  createdAt: Timestamp | null;
};

export type UpdateFile = {
  id: string;
  name: string;
  kind: UpdateKind;
  size: number;
  storagePath: string;
  downloadUrl: string;
  uploadedByUid: string;
  uploadedByName: string | null;
  createdAt: Timestamp | null;
};

export const MAX_UPDATE_BYTES = 50 * 1024 * 1024;

const CONTENT_TYPES: Record<UpdateKind, string> = {
  pdf: 'application/pdf',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

// PowerPoint only. 'pdf' stays in the type for files uploaded before that.
export const UPDATE_ACCEPT = `.ppt,.pptx,${CONTENT_TYPES.ppt},${CONTENT_TYPES.pptx}`;

export function updateKind(fileName: string): UpdateKind | null {
  const ext = fileName.toLowerCase().split('.').pop();
  return ext === 'ppt' || ext === 'pptx' ? ext : null;
}

const foldersCol = (code: string) => collection(db, 'labs', code, 'updateFolders');
const filesCol = (code: string, folderId: string) =>
  collection(db, 'labs', code, 'updateFolders', folderId, 'files');

export function watchFolders(code: string, cb: (f: UpdateFolder[]) => void, onError?: (e: Error) => void) {
  return onSnapshot(
    query(foldersCol(code), orderBy('createdAt', 'desc')),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UpdateFolder)),
    (e) => onError?.(e),
  );
}

export function watchFiles(
  code: string,
  folderId: string,
  cb: (f: UpdateFile[]) => void,
  onError?: (e: Error) => void,
) {
  return onSnapshot(
    query(filesCol(code, folderId), orderBy('createdAt', 'desc')),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as UpdateFile)),
    (e) => onError?.(e),
  );
}

export async function createFolder(code: string, name: string, by: { uid: string; name: string | null }) {
  await addDoc(foldersCol(code), {
    name: name.trim().slice(0, 80),
    createdByUid: by.uid,
    createdByName: by.name,
    createdAt: serverTimestamp(),
  });
}

export async function uploadUpdate(
  code: string,
  folderId: string,
  file: File,
  by: { uid: string; name: string | null },
  onProgress?: (fraction: number) => void,
): Promise<void> {
  const kind = updateKind(file.name);
  if (!kind) throw new Error('Progress updates are PowerPoint files (.ppt or .pptx) only.');
  if (file.size > MAX_UPDATE_BYTES) throw new Error('That file is over 50 MB.');
  const isPpt = kind !== 'pdf';
  if (isPpt) await assertPptAllowed(by.uid);

  const storagePath = `users/${by.uid}/updates/${crypto.randomUUID()}.${kind}`;
  const fileRef = ref(storage, storagePath);
  const task = uploadBytesResumable(fileRef, file, {
    contentType: CONTENT_TYPES[kind],
    contentDisposition: `attachment; filename="${file.name.replace(/"/g, '')}"`,
  });
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => onProgress?.(snap.totalBytes ? snap.bytesTransferred / snap.totalBytes : 0),
      async (e) => reject(isPpt ? await explainPptRefusal(by.uid, e) : e),
      () => resolve(),
    );
  });
  const downloadUrl = await getDownloadURL(fileRef);

  const record = {
    name: file.name,
    kind,
    size: file.size,
    storagePath,
    downloadUrl,
    uploadedByUid: by.uid,
    uploadedByName: by.name,
    createdAt: serverTimestamp(),
  };
  if (isPpt) {
    await savePptRecord(by.uid, doc(filesCol(code, folderId)), record).catch(async (e) => {
      await deleteObject(fileRef).catch(() => {});
      throw e;
    });
  } else {
    await addDoc(filesCol(code, folderId), record);
  }
}

const callDeleteFile = httpsCallable<{ folderId: string; fileId: string }, { ok: true }>(
  functions,
  'deleteUpdateFile',
);
const callDeleteFolder = httpsCallable<{ folderId: string }, { ok: true }>(functions, 'deleteUpdateFolder');

export async function deleteUpdateFile(folderId: string, fileId: string) {
  await callDeleteFile({ folderId, fileId });
}

export async function deleteUpdateFolder(folderId: string) {
  await callDeleteFolder({ folderId });
}
